/**
 * Central SEO contract for datasyte.ai.
 *
 * Every public page gets its head tags from `<Seo />` (see
 * `src/components/seo/Seo.tsx`), which reads the constants and schema builders
 * here. Keeping them in one file means canonical URLs, Open Graph metadata, and
 * structured data can never drift page to page.
 *
 * Rules encoded here:
 *  - canonical URLs are always absolute, always path-only (query strings and
 *    hashes never create a second indexable URL);
 *  - structured data is only emitted when the underlying facts are true — no
 *    Product, Offer, AggregateRating, or outcome claims while AiMediQ™ is
 *    pre-market and not for sale;
 *  - keyword themes are one natural-language theme per page, used to shape real
 *    copy — never repeated for their own sake.
 */

export const SITE_URL = "https://datasyte.ai";
export const SITE_NAME = "datasyteAI™ by PathFree Technologies";
export const ORG_NAME = "PathFree Technologies Corporation";
export const ORG_LEGAL_LOCALITY = "Irvine";
export const ORG_LEGAL_REGION = "CA";

/** 1200×630 sharing card. Descriptive filename, absolute URL for crawlers. */
export const DEFAULT_OG_IMAGE = `${SITE_URL}/social/datasyte-ai-intelligent-resuscitation-platform.jpg`;

/** Absolute URL for a route path. Query strings and hashes are stripped. */
/** Search-result title budget: Google truncates around 60 characters. */
export const TITLE_MAX = 60;
/** Meta description budget: snippets truncate around 158 characters. */
export const DESCRIPTION_MAX = 158;

const truncateAtWord = (value: string, max: number): string => {
  if (value.length <= max) return value;
  const slice = value.slice(0, max - 1);
  const cut = slice.lastIndexOf(" ");
  return `${(cut > max * 0.6 ? slice.slice(0, cut) : slice).replace(/[\s,;:.\-–—]+$/, "")}\u2026`;
};

/**
 * Keeps titles inside the search-result budget without losing the brand
 * suffix: the descriptive part is shortened first, and only a title with no
 * separator is truncated outright.
 */
export const formatTitle = (title: string, max: number = TITLE_MAX): string => {
  const trimmed = title.trim();
  if (trimmed.length <= max) return trimmed;
  const parts = trimmed.split(" | ");
  if (parts.length < 2) return truncateAtWord(trimmed, max);
  // Long brand suffixes are compressed so the descriptive head survives.
  const suffix = parts[parts.length - 1].length > 16 ? "datasyteAI\u2122" : parts[parts.length - 1];
  const head = parts.slice(0, -1).join(" | ");
  const budget = max - suffix.length - 3;
  // A descriptive head that already fits reads better than a truncated one
  // with the brand suffix attached.
  if (budget < 24) return head.length <= max ? head : truncateAtWord(head, max);
  return `${truncateAtWord(head, budget)} | ${suffix}`;
};

/** Keeps meta descriptions inside the snippet budget, ending on a word. */
export const formatDescription = (description: string, max: number = DESCRIPTION_MAX): string => {
  const trimmed = description.trim().replace(/\s+/g, " ");
  if (trimmed.length <= max) return trimmed;
  const sentenceEnd = trimmed.slice(0, max).lastIndexOf(". ");
  if (sentenceEnd >= max * 0.65) return trimmed.slice(0, sentenceEnd + 1);
  return truncateAtWord(trimmed, max);
};

export const canonicalUrl = (path: string): string => {
  const clean = (path || "/").split("?")[0].split("#")[0];
  if (clean === "/" || clean === "") return `${SITE_URL}/`;
  const withSlash = clean.startsWith("/") ? clean : `/${clean}`;
  // No trailing slash on sub-paths: one URL shape sitewide.
  return `${SITE_URL}${withSlash.replace(/\/+$/, "")}`;
};

/** Absolute URL for an asset that already lives under /public. */
export const absoluteAsset = (path: string): string =>
  path.startsWith("http") ? path : `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;

/* ------------------------------------------------------------------ */
/* Structured data                                                     */
/* ------------------------------------------------------------------ */

export type JsonLd = Record<string, unknown>;

export const organizationSchema = (): JsonLd => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: ORG_NAME,
  legalName: ORG_NAME,
  alternateName: ["PathFree", "PathFree Technologies", "PathFree Technologies Corp"],
  url: SITE_URL,
  logo: `${SITE_URL}/icons/icon-512.png`,
  founder: { "@type": "Person", name: "Perry M. Brunette" },
  brand: [
    { "@type": "Brand", name: "datasyteAI" },
    { "@type": "Brand", name: "AiMediQ" },
  ],
  description:
    "PathFree Technologies Corporation develops datasyteAI™, a clinical intelligence layer, and AiMediQ™, an AI-powered code cart for hospital resuscitation events. Both are in pre-market development and validation.",

  address: {
    "@type": "PostalAddress",
    addressLocality: ORG_LEGAL_LOCALITY,
    addressRegion: ORG_LEGAL_REGION,
    addressCountry: "US",
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "sales",
      email: "info@pathfree.com",
      url: `${SITE_URL}/request-demo`,
      availableLanguage: "English",
    },
  ],
  sameAs: [
    "https://pathfree.com",
    "https://www.linkedin.com/company/pathfree-technologies/",
    "https://www.linkedin.com/in/pathfreetech/",
    "https://x.com/PathFreeTech",
    "https://www.einpresswire.com/newsroom/pathfree_technologies/",
    "https://patents.justia.com/assignee/pathfree-technologies-corporation",
  ],
});

export const webSiteSchema = (): JsonLd => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  name: SITE_NAME,
  alternateName: ["PathFree Technologies", "PathFree"],
  url: SITE_URL,
  inLanguage: "en-US",
  publisher: { "@id": `${SITE_URL}/#organization` },
});


export const webPageSchema = ({
  url,
  name,
  description,
  breadcrumbId,
}: {
  url: string;
  name: string;
  description: string;
  breadcrumbId?: string;
}): JsonLd => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${url}#webpage`,
  url,
  name,
  description,
  inLanguage: "en-US",
  isPartOf: { "@id": `${SITE_URL}/#website` },
  ...(breadcrumbId ? { breadcrumb: { "@id": breadcrumbId } } : {}),
});

export const breadcrumbSchema = (
  trail: { label: string; href?: string }[],
  pageUrl?: string,
): JsonLd => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  ...(pageUrl ? { "@id": `${pageUrl}#breadcrumbs` } : {}),
  itemListElement: trail.map((crumb, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: crumb.label,
    ...(crumb.href ? { item: canonicalUrl(crumb.href) } : {}),
  })),
});

export const articleSchema = ({
  url,
  headline,
  description,
  datePublished,
  dateModified,
  authorName,
  section,
  image,
}: {
  url: string;
  headline: string;
  description: string;
  datePublished?: string;
  dateModified?: string;
  authorName?: string;
  section?: string;
  image?: string;
}): JsonLd => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "@id": `${url}#article`,
  mainEntityOfPage: url,
  headline,
  description,
  inLanguage: "en-US",
  ...(datePublished ? { datePublished } : {}),
  ...(dateModified ? { dateModified } : {}),
  ...(section ? { articleSection: section } : {}),
  ...(image ? { image: absoluteAsset(image) } : {}),
  author: authorName
    ? { "@type": "Person", name: authorName, affiliation: { "@id": `${SITE_URL}/#organization` } }
    : { "@id": `${SITE_URL}/#organization` },
  publisher: { "@id": `${SITE_URL}/#organization` },
});

export const faqSchema = (items: { title: string; body: string }[]): JsonLd => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((item) => ({
    "@type": "Question",
    name: item.title,
    acceptedAnswer: { "@type": "Answer", text: item.body },
  })),
});

export const videoSchema = ({
  name,
  description,
  contentUrl,
  embedUrl,
  thumbnailUrl,
  uploadDate,
  duration,
}: {
  name: string;
  description: string;
  contentUrl?: string;
  embedUrl?: string;
  thumbnailUrl?: string;
  uploadDate?: string;
  /** ISO 8601, e.g. PT2M14S. Omit when the real duration is unknown. */
  duration?: string;
}): JsonLd => ({
  "@context": "https://schema.org",
  "@type": "VideoObject",
  name,
  description,
  ...(contentUrl ? { contentUrl: absoluteAsset(contentUrl) } : {}),
  ...(embedUrl ? { embedUrl } : {}),
  ...(thumbnailUrl ? { thumbnailUrl: absoluteAsset(thumbnailUrl) } : {}),
  ...(uploadDate ? { uploadDate } : {}),
  ...(duration ? { duration } : {}),
  publisher: { "@id": `${SITE_URL}/#organization` },
});

export const personSchema = ({
  url,
  name,
  jobTitle,
  description,
  image,
  sameAs,
}: {
  url: string;
  name: string;
  jobTitle?: string;
  description?: string;
  image?: string;
  sameAs?: string[];
}): JsonLd => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": `${url}#person`,
  url,
  name,
  ...(jobTitle ? { jobTitle } : {}),
  ...(description ? { description } : {}),
  ...(image ? { image: absoluteAsset(image) } : {}),
  ...(sameAs && sameAs.length ? { sameAs } : {}),
  worksFor: { "@id": `${SITE_URL}/#organization` },
});

/* ------------------------------------------------------------------ */
/* Page-level keyword themes                                           */
/* ------------------------------------------------------------------ */

/**
 * One natural-language theme per page, plus supporting phrases. These guide
 * headings, lead copy, and meta descriptions. They are never rendered as a
 * keyword list, never repeated for density, and never used to justify a thin
 * page — if there is nothing substantive to say for a theme, the page does not
 * get created.
 */
export const KEYWORD_THEMES: Record<string, { primary: string; supporting: string[] }> = {
  "/": {
    primary: "AI resuscitation platform",
    supporting: ["hospital resuscitation technology", "Code Blue decision support"],
  },
  "/datasyteai": {
    primary: "clinical workflow intelligence",
    supporting: ["resuscitation analytics", "clinical event documentation"],
  },
  "/aimediq": {
    primary: "intelligent code cart",
    supporting: ["emergency-cart inventory", "AI-powered code cart"],
  },
  "/how-it-works": {
    primary: "Code Blue decision support",
    supporting: ["resuscitation documentation", "resuscitation analytics"],
  },
  "/solutions/hospitals-health-systems": {
    primary: "hospital resuscitation technology",
    supporting: ["clinical workflow intelligence"],
  },
  "/solutions/resuscitation-teams": {
    primary: "Code Blue decision support",
    supporting: ["resuscitation documentation"],
  },
  "/solutions/quality-patient-safety": {
    primary: "resuscitation analytics",
    supporting: ["clinical event documentation"],
  },
  "/solutions/pharmacy-clinical-operations": {
    primary: "emergency-cart inventory",
    supporting: ["code cart readiness"],
  },
  "/solutions/it-security-integration": {
    primary: "clinical workflow intelligence",
    supporting: ["EHR interoperability for resuscitation"],
  },
  "/solutions/training-preparedness": {
    primary: "Code Blue training",
    supporting: ["resuscitation simulation"],
  },
  "/evidence": {
    primary: "resuscitation analytics",
    supporting: ["in-hospital cardiac arrest evidence"],
  },
  "/evidence/clinical-approach": {
    primary: "Code Blue decision support",
    supporting: ["clinical decision-support boundaries"],
  },
  "/evidence/research-publications": {
    primary: "resuscitation research",
    supporting: ["in-hospital cardiac arrest literature"],
  },
  "/evidence/regulatory-quality": {
    primary: "medical device regulatory status",
    supporting: ["quality and cybersecurity practices"],
  },
  "/resources": {
    primary: "resuscitation documentation",
    supporting: ["Code Blue training", "clinical event documentation"],
  },
  "/request-demo": {
    primary: "resuscitation platform demo",
    supporting: ["Code Blue decision support walkthrough"],
  },
};
